import { Node, mergeAttributes } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import { DOMSerializer } from '@tiptap/pm/model';
import { attachMemberCardTiltHandlers } from '../components/calendar/memberCardEffectStyles';

/** Atom block for tour-party member cards (preserves legacy HTML). */
export const WriterMemberNode = Node.create({
  name: 'writerMember',
  group: 'block',
  atom: true,
  draggable: true,
  selectable: true,

  addAttributes() {
    return {
      memberId: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-member-id'),
        renderHTML: (attrs) => (attrs.memberId ? { 'data-member-id': attrs.memberId } : {}),
      },
      blockHtml: {
        default: '',
        parseHTML: (el) => el.outerHTML,
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div.vrc-writer-member',
        getAttrs: (dom) => ({
          memberId: dom.getAttribute('data-member-id'),
          blockHtml: dom.outerHTML,
        }),
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { class: 'vrc-writer-member-slot' })];
  },

  addNodeView() {
    return ({ node, getPos, editor }) => {
      const wrap = document.createElement('div');
      wrap.innerHTML = node.attrs.blockHtml || '';
      const dom = wrap.firstElementChild || wrap;
      dom.classList.add('vrc-writer-member-nodeview');

      const syncBlockHtml = () => {
        const pos = getPos();
        if (typeof pos !== 'number') return;
        const nextHtml = dom.outerHTML;
        if (nextHtml === node.attrs.blockHtml) return;
        const { tr } = editor.state;
        tr.setNodeMarkup(pos, undefined, {
          ...node.attrs,
          blockHtml: nextHtml,
          memberId: dom.getAttribute('data-member-id') || node.attrs.memberId,
        });
        editor.view.dispatch(tr);
      };

      const onNotesInput = () => syncBlockHtml();

      const onRemoveClick = (event) => {
        const btn = event.target?.closest?.('.vrc-writer-member-remove');
        if (!btn || !dom.contains(btn)) return;
        event.preventDefault();
        event.stopPropagation();
        const pos = getPos();
        if (typeof pos !== 'number') return;
        editor
          .chain()
          .focus()
          .deleteRange({ from: pos, to: pos + node.nodeSize })
          .run();
      };

      dom.querySelector('.vrc-writer-member-notes')?.addEventListener('input', onNotesInput);
      dom.addEventListener('click', onRemoveClick);

      const unbindTilt = attachMemberCardTiltHandlers(dom);

      return {
        dom,
        update(updatedNode) {
          return updatedNode.type.name === 'writerMember';
        },
        destroy() {
          dom.querySelector('.vrc-writer-member-notes')?.removeEventListener('input', onNotesInput);
          dom.removeEventListener('click', onRemoveClick);
          unbindTilt?.();
        },
      };
    };
  },
});

export function createWriterEditorExtensions({ placeholder }) {
  return [
    StarterKit.configure({
      heading: { levels: [1, 2] },
    }),
    Underline,
    Link.configure({
      openOnClick: false,
      HTMLAttributes: {
        target: '_blank',
        rel: 'noopener noreferrer',
      },
    }),
    TextAlign.configure({
      types: ['heading', 'paragraph'],
    }),
    Placeholder.configure({
      placeholder: placeholder || 'Write rider notes…',
      emptyEditorClass: 'vrc-writer-editor-empty',
    }),
    WriterMemberNode,
  ];
}

/** Serialize document to HTML string (member blocks use stored blockHtml). */
export function serializeWriterDoc(editor) {
  if (!editor?.state?.doc || typeof document === 'undefined') return '';
  const serializer = DOMSerializer.fromSchema(editor.schema);
  const container = document.createElement('div');

  editor.state.doc.forEach((child) => {
    if (child.type.name === 'writerMember') {
      const temp = document.createElement('div');
      temp.innerHTML = child.attrs.blockHtml || '';
      while (temp.firstChild) {
        container.appendChild(temp.firstChild);
      }
      return;
    }
    try {
      const dom = serializer.serializeNode(child);
      container.appendChild(dom);
    } catch {
      // skip unsupported nodes
    }
  });

  return container.innerHTML;
}
